/* eslint no-use-before-define: "error" */
import deepExtend from "deep-extend";
import downloadSvgAndAddViewbox from "utils/downloadSvgAndAddViewbox";
import updateCustomStylesheet from "utils/updateCustomStylesheet";

import cloneDeep from "lodash/cloneDeep";
import defaultsDeep from "lodash/defaultsDeep";
import get from "lodash/get";

// Make sure the trimmed logo is included in the project. It will be used as the fallback if the
// source json does not include a URL for the story logo
import verseLogoSvg from "img/verse-logo-trimmed.svg";

export default async function postprocessNormalizedStory(rawData) {
  let data = cloneDeep(rawData);

  data = defaultsDeep({}, data, {
    customizations: {
      playerNavigation: {
        embeddedMonogramClickNavigatesToVerseDotCom: true,
        showHamburger: true,
      },
      storyCover: {
        imageUrl: null,
        svgContent: null,
        minimal: false,
        imageMultiplier: 1,
        viewStoryButtonCopy: "View Story",
      },
      userAvatar: {
        image: null,
        initials: null,
        color: "#1DA2FF",
      },
    },
  });

  // Make sure the logo url field is populated
  data = {
    ...data,
    hasCustomMonogram: !!get(data, "logoUrl"),
    logoHeightMultiplier: get(data, "logoHeightMultiplier") || 1,
    logoUrl: get(data, "logoUrl"),
  };

  const logoExtension =
    data.logoUrl && data.logoUrl.split(".").pop().toLowerCase();
  const logoIsImage =
    logoExtension === "png" ||
    logoExtension === "jpg" ||
    logoExtension === "jpeg";
  const logoIsSvg = logoExtension === "svg";

  function getUserAvatar() {
    const userAvatar = data.customizations.userAvatar.image;
    const userInitials = data.customizations.userAvatar.initials;
    const userColor = data.customizations.userAvatar.color || "37a0d7";

    if (userAvatar) {
      const markup = `
                <div class="generic-avatar">
                    <img src="${userAvatar}" />
                </div>
            `;

      return markup;
    }
    if (userInitials && userInitials.length) {
      const markup = `
                <div class="generic-avatar font-size--icon--large" style="background-color: #${userColor.replace(
                  /#/g,
                  "",
                )}">
                    <span>${userInitials[0]}</span>
                </div>
            `;

      return markup;
    }

    return null;
  }

  // 1. png/jpg custom logo
  if (logoIsImage) {
    data.logoContent = data.logoUrl;
    data.hasImageLogo = true;
    // 2. svg custom logo
  } else if (logoIsSvg) {
    try {
      data.logoContent = await downloadSvgAndAddViewbox(data.logoUrl);
    } catch {
      data.logoContent = getUserAvatar();
    }
  } else {
    // 3. custom avatar or initials
    data.logoContent = getUserAvatar();
  }

  // 4. verse logo (legacy)
  if (!data.logoContent) {
    data.logoUrl = verseLogoSvg;
    const text = await downloadSvgAndAddViewbox(data.logoUrl);
    data.logoContent = text;
  }

  // Fetch and save the custom story cover svg
  const imageUrl =
    data?.customizations?.storyCover?.imageUrl ||
    data?.customizations?.storyCover?.svgUrl;
  if (imageUrl?.match(/\.svg$/)) {
    data.customizations.storyCover.imageUrl = imageUrl;
  }
  const coverImage = get(data, "customizations.storyCover.imageUrl");
  if (coverImage) {
    const coverExtension =
      coverImage && coverImage.split(".").pop().toLowerCase();
    const coverImageIsSvg = coverExtension && coverExtension === "svg";

    if (coverImageIsSvg) {
      try {
        // Try to download the logo provided from the CMS
        const text = await downloadSvgAndAddViewbox(coverImage);
        data = deepExtend({}, data, {
          customizations: {
            storyCover: {
              svgContent: text,
            },
          },
        });
      } catch (error) {
        console.error("postprocessNormalizedStory", error); //eslint-disable-line no-console
      }
    }

    // png storyCover.imageUrl's will work as-is
  }

  // fallback to convert svgMultiplier to imageMultiplier
  if (get(data, "customizations.storyCover.svgMultiplier")) {
    data = deepExtend({}, data, {
      customizations: {
        storyCover: {
          imageMultiplier: get(data, "customizations.storyCover.svgMultiplier"),
        },
      },
    });
  }

  // Update custom stylesheet
  if (data.customColors) {
    try {
      updateCustomStylesheet(data.customColors);
    } catch (error) {
      console.error("postprocessNormalizedStory", error); //eslint-disable-line no-console
    }
  }

  return data;
}
